import { MatchSearchResults } from '@/components/match/match-search-results';
import { ManualPickForm } from '@/components/opportunity/manual-pick-form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { trpc } from '@/lib/trpc';
import type { MatchSearchResult } from '@tennis-scanner/shared';
import { useEffect, useMemo, useRef, useState } from 'react';

// Manual pick — operator workflow when a recommendation comes from outside
// the model (WinnerOdds export, Telegram tip). One-page chained workflow:
//   1. Type 2-4 chars of player surname.
//   2. Click match.
//   3. Fill market / selection / odds / window → Crear pick.
//   4. Form clears, focus jumps back to the search box for the next pick.
//
// Backend (matches.search) tokenizes the query on whitespace and OR-matches
// each token across p1 + p2 + tournament name. So "stefanos tsitsipas"
// finds "S. Tsitsipas" (matches via the surname token).

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function ManualPickPage() {
  const [query, setQuery] = useState('');
  const [pickedMatch, setPickedMatch] = useState<MatchSearchResult | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 150);
  const trimmed = debouncedQuery.trim();
  const isQueryReady = trimmed.length >= 2;

  // LIVE + UPCOMING only. Window: 6h back (covers in-play tennis matches —
  // 5h max set duration plus headroom) to 7d forward. Past completed
  // matches whose status the connector hasn't ticked to 'completed' would
  // otherwise saturate the 50-row page.
  const fromUtc = useMemo(() => new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), []);
  const toUtc = useMemo(() => new Date(Date.now() + 7 * ONE_DAY_MS).toISOString(), []);

  // Only fire the search once the operator has typed at least 2 chars. Empty
  // input shows a hint, not a default-50 list — the operator's flow is
  // "tap surname, see results", not "browse the radar".
  const search = trpc.matches.search.useQuery(
    { query: trimmed, fromUtc, toUtc, limit: 50 },
    { staleTime: 15_000, enabled: isQueryReady },
  );

  // After a pick is created, clear search + refocus so the operator can
  // chain picks without touching the mouse.
  function handleCreated(): void {
    setPickedMatch(null);
    setQuery('');
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }

  const totalCount = isQueryReady ? (search.data?.length ?? 0) : 0;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-brand text-3xl tracking-[0.25em] text-foreground">CARGAR PICK</h1>
        <p className="text-xs text-muted-foreground">
          Buscá el partido, completá los datos y el scanner empieza a vigilar las cuotas. Aparece
          como alerta apenas alguna casa pague ≥ tu target. Tip: api-tennis guarda los nombres como{' '}
          <span className="font-mono">"S. Tsitsipas"</span> — buscá por apellido.
        </p>
      </header>

      <Card className="space-y-3 p-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            1. Partido
          </h2>
          <span className="font-mono text-xs text-muted-foreground">
            {!isQueryReady
              ? 'escribí 2+ caracteres'
              : search.isLoading
                ? 'buscando…'
                : `${totalCount} ${totalCount === 1 ? 'resultado' : 'resultados'}`}
          </span>
        </div>
        <Input
          ref={searchInputRef}
          placeholder="Apellido del jugador o nombre del torneo (mín 2 caracteres)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {/* Don't render any list until the operator has typed enough. Empty
            state is a soft hint, not a 50-row dump of ITF noise. */}
        {!isQueryReady ? (
          <p className="rounded-lg border border-dashed border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
            Escribí al menos 2 caracteres para buscar (apellido del jugador o nombre del torneo).
          </p>
        ) : (
          <MatchSearchResults
            results={search.data ?? []}
            isLoading={search.isLoading}
            selectedId={pickedMatch?.id ?? null}
            onSelect={(m) => setPickedMatch(m)}
            emptyHint="Sin coincidencias para esa búsqueda."
          />
        )}
      </Card>

      <Card className={pickedMatch ? 'p-4' : 'pointer-events-none p-4 opacity-50'}>
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            2. Pick
          </h2>
          {!pickedMatch && (
            <Alert variant="info">
              <AlertTitle>Seleccioná un partido</AlertTitle>
              <AlertDescription>
                El form se habilita cuando haya un partido elegido arriba.
              </AlertDescription>
            </Alert>
          )}
          {pickedMatch && (
            <>
              <PickedMatchSummary match={pickedMatch} onClear={() => setPickedMatch(null)} />
              <ExistingPicksWarning matchId={pickedMatch.id} />
              <Separator />
              <ManualPickForm match={pickedMatch} onCreated={handleCreated} />
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

// Soft warning when the picked match already has active signals. Doesn't
// block — the operator may want to add a different selection / market /
// supersede an existing pick. Just makes the situation visible.
function ExistingPicksWarning({ matchId }: { matchId: string }) {
  const q = trpc.signals.list.useQuery(
    { matchId, limit: 10, includeSuperseded: false },
    { staleTime: 10_000 },
  );
  const count = q.data?.length ?? 0;
  if (q.isLoading || count === 0) return null;
  return (
    <Alert variant="info">
      <AlertTitle>
        Ya hay {count} {count === 1 ? 'pick activo' : 'picks activos'} en este partido
      </AlertTitle>
      <AlertDescription>
        Podés crear otro (otro mercado / selección) o usar el botón "Corregir" en la card del pick
        existente para superseder.
      </AlertDescription>
    </Alert>
  );
}

function PickedMatchSummary({
  match,
  onClear,
}: {
  match: MatchSearchResult;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-popover p-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">
          {match.p1Name ?? 'p1'} vs {match.p2Name ?? 'p2'}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {match.tournamentName ?? '—'} · {match.surface ?? '—'} · BO{match.bestOf}
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={onClear}>
        Cambiar
      </Button>
    </div>
  );
}

// Tiny inline debounce so we don't fire matches.search on every keystroke.
function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
